/**
 * Fake playthrough simulator — walks chapters 0..(target-1) using quiz
 * answers to resolve decisions, producing a game state snapshot that
 * the player can resume from as if they'd actually played.
 *
 * Mirrors processBackstory in ChatVisualNovel but across multiple
 * chapters and honours quiz-chosen decision values instead of
 * always picking the first variant.
 */

function formatGameTimeStr(gt) {
  const h = String(gt.hours).padStart(2, '0');
  const m = String(gt.minutes).padStart(2, '0');
  return `${h}:${m}`;
}

function applyTimeskip(gt, mode, hours, minutes) {
  let newH = gt.hours;
  let newM = gt.minutes;
  let newDay = gt.day;
  if (mode === 'set') {
    const targetTotal = hours * 60 + minutes;
    const currentTotal = gt.hours * 60 + gt.minutes;
    if (targetTotal <= currentTotal) newDay += 1;
    newH = hours;
    newM = minutes;
  } else {
    newM += minutes;
    newH += hours;
    newH += Math.floor(newM / 60);
    newM = newM % 60;
    if (newH >= 24) {
      newDay += Math.floor(newH / 24);
      newH = newH % 24;
    }
  }
  return { hours: newH, minutes: newM, day: newDay };
}

function getLocalizedText(node, lang) {
  if (lang === 'en' && node.data?.text_en) return node.data.text_en;
  return node.data?.text || '';
}

function getLocalizedVariants(node, lang) {
  const ru = node.data?.variants || [];
  const en = node.data?.variants_en || [];
  if (lang === 'en' && en.length) {
    return en.map((v, i) => v || ru[i] || '');
  }
  return ru;
}

/**
 * Look ahead from a sfDialogue node: if picking variant `i` lands on a
 * sfDecision with a known quiz answer, prefer that variant so the
 * decision node's incoming handle records the desired option.
 */
function pickForcedVariant(nodeId, nodes, edges, quizAnswers) {
  const outs = edges.filter((e) => e.source === nodeId && (e.sourceHandle || '').startsWith('choice-'));
  for (const e of outs) {
    const target = nodes.find((n) => n.id === e.target);
    if (!target || target.type !== 'sfDecision') continue;
    const name = target.data?.decisionName;
    const opts = target.data?.options || [];
    const answer = name ? quizAnswers[name] : null;
    if (!answer) continue;
    const desiredInIdx = opts.indexOf(answer);
    if (desiredInIdx < 0) continue;
    // Which choice-i edge lands on decision-in-{desiredInIdx}?
    const match = outs.find((x) => {
      if (x.target !== target.id) return false;
      const th = x.targetHandle || '';
      return th === `decision-in-${desiredInIdx}`;
    });
    if (match) {
      const ih = (match.sourceHandle || '').replace('choice-', '');
      const idx = parseInt(ih, 10);
      if (!isNaN(idx)) return idx;
    }
  }
  return -1;
}

function walkChapter(storyflow, quizAnswers, lang, state, allStoryflows) {
  const nodes = storyflow?.graph?.nodes || [];
  const edges = storyflow?.graph?.edges || [];
  if (!nodes.length) return { nextStoryflowId: null, nextNodeId: null };

  const findNode = (id) => nodes.find((n) => n.id === id);
  const followFlowOut = (fromId) => {
    const edge = edges.find((e) => e.source === fromId && (e.sourceHandle === 'flow-out' || !e.sourceHandle));
    return edge ? edge.target : null;
  };

  const startNode = nodes.find((n) => n.type === 'sfStart');
  if (!startNode) return { nextStoryflowId: null, nextNodeId: null };

  const visited = new Set();
  let currentId = startNode.id;
  let nextStoryflowId = null;
  let nextNodeId = null;

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    state.nodeHistory.push(currentId);
    const node = findNode(currentId);
    if (!node) break;

    if (node.type === 'sfStart') {
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfDialogue') {
      const speakerId = node.data?.speakerId;
      if (speakerId) {
        state.lastSpeakerId = speakerId;
        state.unlockedContacts[speakerId] = true;
      }
      const contactId = speakerId || state.lastSpeakerId;
      if (contactId) {
        const rawText = getLocalizedText(node, lang);
        const messages = rawText.split('\n').filter(Boolean);
        if (!state.chatHistories[contactId]) state.chatHistories[contactId] = [];
        messages.forEach((msg) => {
          state.chatHistories[contactId].push({
            text: msg, isUser: false, time: formatGameTimeStr(state.gameTime),
            speakerName: node.data?.speakerName
          });
        });
      }
      const variants = getLocalizedVariants(node, lang).filter(Boolean);
      if (variants.length > 0) {
        const forcedIdx = pickForcedVariant(currentId, nodes, edges, quizAnswers);
        const pickIdx = forcedIdx >= 0 && forcedIdx < variants.length ? forcedIdx : 0;
        if (contactId && state.chatHistories[contactId]) {
          state.chatHistories[contactId].push({
            text: variants[pickIdx], isUser: true, time: formatGameTimeStr(state.gameTime)
          });
        }
        // Apply per-choice relationship delta
        const relChanges = node.data?.relationships;
        if (Array.isArray(relChanges) && contactId) {
          const delta = relChanges[pickIdx];
          if (typeof delta === 'number' && delta !== 0) {
            state.relationships[contactId] = (state.relationships[contactId] || 0) + delta;
          }
        }
        const edge = edges.find((e) => e.source === currentId && e.sourceHandle === `choice-${pickIdx}`);
        currentId = edge ? edge.target : followFlowOut(currentId);
      } else {
        currentId = followFlowOut(currentId);
      }
    } else if (node.type === 'sfImage') {
      const contactId = node.data?.characterId || state.lastSpeakerId;
      const imageUrl = node.data?.imageUrl || node.data?.imagePreview512;
      if (contactId && imageUrl) {
        if (!state.chatHistories[contactId]) state.chatHistories[contactId] = [];
        state.chatHistories[contactId].push({
          image: imageUrl,
          isUser: !!node.data?.playerSends,
          time: formatGameTimeStr(state.gameTime)
        });
        state.galleryUnlocks.push({
          storyflowId: storyflow.id,
          nodeId: node.id,
          imageUrl,
          imagePreview512: node.data?.imagePreview512,
          playerSends: !!node.data?.playerSends,
          unlockedAt: Date.now() - (1000000 - state.galleryUnlocks.length)
        });
      }
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfVideo') {
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfDecision') {
      const name = node.data?.decisionName || node.id;
      const opts = node.data?.options || [];
      // Find the edge we actually traversed via nodeHistory; edges.find by
      // target alone picks an arbitrary match when multiple choices land here.
      const prevId = state.nodeHistory[state.nodeHistory.length - 2];
      const inEdge = edges.find(
        (e) => e.source === prevId && e.target === currentId &&
               (e.targetHandle || '').startsWith('decision-in-')
      );
      const inIdx = inEdge ? parseInt((inEdge.targetHandle || '').split('-').pop(), 10) || 0 : 0;
      // Honour the quiz answer over whatever incoming handle we took —
      // lookahead may have failed to align variant indices for this path.
      const answered = quizAnswers[name];
      let outIdx = inIdx;
      if (answered && opts.includes(answered)) {
        outIdx = opts.indexOf(answered);
      }
      state.decisions[name] = opts[outIdx] || opts[inIdx] || opts[0] || 'True';
      const edge = edges.find((e) => e.source === currentId && e.sourceHandle === `decision-out-${outIdx}`);
      currentId = edge ? edge.target : followFlowOut(currentId);
    } else if (node.type === 'sfBranch') {
      const ref = node.data?.decisionRef;
      const switchOpts = node.data?.switchOptions || [];
      const stored = ref ? state.decisions[ref] : null;
      let matchIdx = stored ? switchOpts.indexOf(stored) : -1;
      if (matchIdx < 0) {
        const falseIdx = switchOpts.indexOf('False');
        matchIdx = falseIdx >= 0 ? falseIdx : 0;
      }
      const edge = edges.find((e) => e.source === currentId && e.sourceHandle === `switch-out-${matchIdx}`);
      currentId = edge ? edge.target : followFlowOut(currentId);
    } else if (node.type === 'sfTimeskip') {
      const mode = node.data?.timeskipMode || 'skip';
      state.gameTime = applyTimeskip(state.gameTime, mode, node.data?.hours || 0, node.data?.minutes || 0);
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfIntroduce') {
      const charId = node.data?.characterId;
      if (charId) state.unlockedContacts[charId] = true;
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfStatus') {
      const charId = node.data?.characterId || state.lastSpeakerId;
      if (charId) state.contactStatuses[charId] = node.data?.statusEmoji || '';
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfInsta') {
      const charId = node.data?.characterId;
      const imageUrl = node.data?.imageUrl || node.data?.imagePreview512;
      if (charId && imageUrl) {
        state.instaPosts.push({
          id: `ipost-node-${currentId}`,
          characterId: charId,
          accountId: '',
          section: node.data?.instaSection || 'public',
          imageUrl,
          imagePreview512: node.data?.imagePreview512 || imageUrl,
          caption: node.data?.caption || '',
          likes: node.data?.likes || 0,
          createdAt: new Date().toISOString()
        });
      }
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfRpSwitch') {
      const edge = edges.find((e) => e.source === currentId && e.sourceHandle === 'rp-out-pass');
      currentId = edge ? edge.target : followFlowOut(currentId);
    } else if (node.type === 'sfNextChapter' || node.type === 'sfNextFlow') {
      const targetName = node.data?.targetChapterName || node.data?.chapterName;
      if (targetName && allStoryflows) {
        const target = allStoryflows.find((s) => s.name === targetName);
        if (target) {
          nextStoryflowId = target.id;
          const startN = (target.graph?.nodes || []).find((n) => n.type === 'sfStart');
          nextNodeId = startN?.id || null;
        }
      }
      currentId = null;
    } else {
      currentId = followFlowOut(currentId);
    }
  }

  return { nextStoryflowId, nextNodeId };
}

/**
 * Simulate a playthrough from chapter 0 up to (but not including) the
 * target chapter. Returns a game state blob compatible with the save
 * format used by ChatVisualNovel's handleLoadSave/auto-load.
 */
export function simulatePlaythrough({
  allStoryflows,
  quizAnswers,
  targetChapterNumber,
  lang = 'ru'
}) {
  const state = {
    chatHistories: {},
    unlockedContacts: {},
    galleryUnlocks: [],
    instaPosts: [],
    contactStatuses: {},
    gameTime: { hours: 14, minutes: 0, day: 0 },
    decisions: { ...(quizAnswers || {}) },
    nodeHistory: [],
    relationships: {},
    lastSpeakerId: null
  };

  const sortedChapters = [...(allStoryflows || [])]
    .filter((s) => (s.number ?? null) !== null && (s.number ?? 0) < targetChapterNumber)
    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

  const unlockedChapters = [];
  for (const chapter of sortedChapters) {
    walkChapter(chapter, quizAnswers || {}, lang, state, allStoryflows);
    unlockedChapters.push(chapter.id);
  }

  // Entry point = start node of target chapter.
  const target = (allStoryflows || []).find((s) => (s.number ?? null) === targetChapterNumber);
  const targetStart = target ? (target.graph?.nodes || []).find((n) => n.type === 'sfStart') : null;

  if (target) unlockedChapters.push(target.id);

  return {
    chatHistories: state.chatHistories,
    unlockedContacts: state.unlockedContacts,
    contactStatuses: state.contactStatuses,
    galleryUnlocks: state.galleryUnlocks,
    instaPosts: state.instaPosts,
    viewedStories: {},
    gameTime: state.gameTime,
    decisions: state.decisions,
    nodeHistory: state.nodeHistory,
    relationships: state.relationships,
    unlockedChapters,
    currentStoryflowId: target?.id || '',
    currentNodeId: targetStart?.id || '',
    currentChapter: target?.name || '',
    activeContactId: state.lastSpeakerId || null,
    wallpaper: null,
    currentChoices: null
  };
}
